import { FormProvider } from "react-hook-form";
import {
  AlertDialog,
  AlertDialogTrigger,
} from "@/view/components/ui/alert-dialog";
import { Card } from "@/view/components/ui/card";
import { Switch } from "@/view/components/ui/switch";
import { TriStateSwitch } from "@/view/components/TriStatesSwitch";
import { Spinner } from "@/view/components/ui/spinner";
import ButtonBack from "@/view/components/ButtonBack";
import { useAuth } from "@/app/hooks/useAuth";
import { PartnerType } from "@/app/utils/partners.enum";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { TriStateEnum } from "@/app/entities/TriState";
import { usePartnersPageController } from "./usePartnersPageController";
import { EnableAndDisablePartners } from "./components/EnableAndDisablePartners";
import { EditPartner } from "./components/EditPartner";
import { TablePage } from "../TablePage";
import { AddTableCard } from "../TablePage/components/AddTableCard";

export default function PartnersPage() {
  const {
    productsConfig,
    isLoading,
    PERMISSIONS,
    selectedPartner,
    setSelectedPartner,
    selectedPartnerData,
    setSelectedPartnerData,
    methods,
    handleProductStateChange,
    partnersConfig,
  } = usePartnersPageController();

  const { isSuperAdmin } = useAuth();

  const getProductState = (product: any): TriStateEnum => {
    return product.state || TriStateEnum.OFF;
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (selectedPartner === PartnerType.FGTS) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex w-full items-center justify-between">
          <ButtonBack
            label="Produtos - FGTS"
            action={() => setSelectedPartner(null)}
          />
          {PERMISSIONS.tables.create && (
            <FormProvider {...methods}>
              <AddTableCard />
            </FormProvider>
          )}
        </div>

        <TablePage />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
      {Object.entries(productsConfig?.products || {})
        .filter(([_, productDetail]) => {
          if (isSuperAdmin) return true;
          return (
            productDetail.state === TriStateEnum.ON ||
            productDetail.state === TriStateEnum.FULL
          );
        })
        .map(([partnerKey, productDetail]) => {
          const partner = partnerKey as PartnerType;
          const config = partnersConfig[partner];

          if (!config) return null;
          const isFGTS = partner === PartnerType.FGTS;
          const itemsToShow = isFGTS
            ? productDetail.itens.slice(0, 3)
            : config.itemsToShow(productDetail.itens);

          return (
            <AlertDialog key={partner}>
              <AlertDialogTrigger asChild>
                <Card
                  className="cursor-pointer p-6 transition-shadow hover:shadow-lg"
                  onClick={() => {
                    if (isFGTS) {
                      setSelectedPartner(PartnerType.FGTS);
                    } else {
                      setSelectedPartnerData({
                        partner,
                        itens: productDetail.itens,
                        tableId: productDetail.tableId || productDetail._id,
                        delay: productDetail.delay,
                        isActive: productDetail.isActive,
                        state: productDetail.state,
                      });
                    }
                  }}
                >
                  <div
                    className="flex h-[110px] w-full items-center justify-center overflow-hidden rounded-lg"
                    style={{ backgroundColor: getPartnerBgColor(partner) }}
                  >
                    <img
                      alt="parceiro img"
                      src={getPartnerImage(partner)}
                      className="h-full w-auto object-contain"
                      style={{ aspectRatio: "2/1" }}
                    />
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <p className="font-bold">{partner}</p>

                    <div className="flex flex-1 justify-end">
                      {isSuperAdmin ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <TriStateSwitch
                            value={getProductState(productDetail)}
                            onChange={(state) =>
                              handleProductStateChange(partner, state)
                            }
                          />
                        </div>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <div onClick={(e) => e.stopPropagation()}>
                              <Switch
                                checked={
                                  productDetail.state === TriStateEnum.FULL
                                }
                              />
                            </div>
                          </AlertDialogTrigger>

                          <EnableAndDisablePartners
                            isActive={productDetail.state === TriStateEnum.FULL}
                            id={partner}
                          />
                        </AlertDialog>
                      )}
                    </div>
                  </div>



                  <div className="mt-1 flex items-center justify-between">
                    <p className="text-sm font-medium text-black03">
                      {config.title}
                    </p>
                    <p className="font-medium text-black03">
                      {productDetail.itens.length}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-col gap-1">
                    {itemsToShow.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <p className="text-sm font-medium text-black04">
                          {config.renderLabel(item, index, itemsToShow)}
                        </p>
                        <div className="flex-1 border-t border-dashed border-black04"></div>
                        <p className="text-sm font-medium">
                          {config.renderValue(item)}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              </AlertDialogTrigger>

              <EditPartner partnerData={selectedPartnerData} />
            </AlertDialog>
          );
        })}
    </div>
  );
}
